import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  memo,
  forwardRef,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getExpandedRowModel,
  Column,
  ColumnFiltersState,
  Row,
} from "@tanstack/react-table";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import AutoSizer from "react-virtualized-auto-sizer";
import TableTotal from "../table/table-total/tableTotal";
import Loading from "../loading/loading";
import { makeId, getHeightBeforeEndOfPage } from "../../hooks/utils";
import useWindowSize from "../../hooks/useWindowsSize";
import { cn } from "../../services/common/className";
import { Filter } from "../table/filters/filters";
import SelectedBar from "../table/selected-bar/selectedBar";
import { ReactComponent as ArrowUpIcon } from "../../images/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../images/arrow_down.svg";
import type { DataTableProps } from "./dataTableTypes";
import DataTableToolbar from "./dataTableToolbar";
import "./dataTable.scss";

const CHECKBOX_COLUMN_SIZE = 44;
const TOOLBAR_COLUMN_WIDTH = 44;
const SCROLL_SPACER_WIDTH = 16;
const DEFAULT_MIN_COLUMN_WIDTH = 48;
const DEFAULT_MAX_COLUMN_WIDTH = 800;
const DEFAULT_ROW_HEIGHT = 40;

const getTableHeight = (
  tableId: string,
  windowHeight: number,
  strictMinTableHeight: boolean,
  minTableHeight?: number
) => {
  const height = getHeightBeforeEndOfPage(
    `.xgs-data-table.${tableId}`,
    300,
    windowHeight,
    40
  );
  const minHeight = minTableHeight ?? 100;
  return !strictMinTableHeight && height > minHeight ? height - 20 : minHeight;
};

const scrollbarWidth = () => {
  const scrollDiv = document.createElement("div");
  scrollDiv.setAttribute(
    "style",
    "width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;"
  );
  document.body.appendChild(scrollDiv);
  const w = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return w;
};

const DataTable = memo(
  forwardRef<HTMLDivElement, DataTableProps<any>>(function DataTable(props, ref) {
    const {
      columns,
      data,
      cursorPointer,
      ignoreCellClick,
      isLoading,
      isBlocked,
      keepTableOnLoading,
      noResultsText,
      noResultsComponent,
      onRowClicked,
      enableRowSelection,
      rowClickTogglesSelection,
      onSelectedRowsChange,
      autoResetSelectedRows,
      responsive,
      sorting,
      totals,
      enableMultiSort,
      enableSortingRemoval,
      filters,
      interactiveHeader = true,
      stickyHeaders = true,
      showColumnSelector,
      columnSelectorOptions,
      enableColumnResize = true,
      enableColumnReorder = true,
      autoHeight = true,
      rowHeight = DEFAULT_ROW_HEIGHT,
      minTableHeight,
      strictMinTableHeight = false,
      listHeightOverride,
      shrinkToContent,
      stickyLeadingColumnId,
      renderSubComponent,
      getRowCanExpand,
      renderTableFooter,
      infiniteScroll,
      infiniteScrollHasNext,
      infiniteScrollLoading,
      onInfiniteScroll,
    } = props;

    const {
      columnVisibility,
      onColumnVisibilityChange,
      columnOrder,
      onColumnOrderChange,
      columnSizing,
      onColumnSizingChange,
    } = props;

    const [tableId] = useState(() => `xgs-data-table-${makeId(5)}`);
    const scrollBarSize = useMemo(() => scrollbarWidth(), []);
    const selectedBarRef = useRef<HTMLDivElement>(null);
    const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);
    const windowHeight = useWindowSize()[1];
    const dataListRef = useRef<FixedSizeList>(null);
    const selectionListRef = useRef<FixedSizeList>(null);
    const scrollSyncRef = useRef(false);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const useUnifiedExpandableScroll = !!(renderSubComponent && stickyLeadingColumnId);

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      ...(getRowCanExpand && { getRowCanExpand }),
      ...(filters && {
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
      }),
      getSortedRowModel: getSortedRowModel(),
      ...(renderSubComponent && { getExpandedRowModel: getExpandedRowModel() }),
      enableMultiSort: !!enableMultiSort,
      enableSortingRemoval: enableSortingRemoval,
      isMultiSortEvent: () => !!enableMultiSort,
      state: {
        ...(columnVisibility !== undefined && { columnVisibility }),
        ...(columnOrder !== undefined && { columnOrder }),
        ...(columnSizing !== undefined && { columnSizing }),
        ...(filters && { columnFilters }),
      },
      onColumnFiltersChange: setColumnFilters,
      // Only pass controlled handlers when provided. Passing `undefined` overrides
      // TanStack's default makeStateUpdater and makes setColumnOrder / visibility a no-op.
      ...(onColumnVisibilityChange != null && {
        onColumnVisibilityChange: onColumnVisibilityChange as any,
      }),
      ...(onColumnOrderChange != null && {
        onColumnOrderChange: onColumnOrderChange as any,
      }),
      ...(onColumnSizingChange != null && { onColumnSizingChange }),
      columnResizeMode: "onChange",
      enableColumnResizing: enableColumnResize,
      defaultColumn: {
        minSize: DEFAULT_MIN_COLUMN_WIDTH,
        maxSize: DEFAULT_MAX_COLUMN_WIDTH,
        filterFn: "includesString",
      },
      enableRowSelection: enableRowSelection ?? true,
    });

    const {
      getHeaderGroups,
      getRowModel,
      getSelectedRowModel,
      getFilteredSelectedRowModel,
      getIsAllRowsSelected,
      getToggleAllRowsSelectedHandler,
      toggleAllRowsSelected,
      resetRowSelection,
      getAllColumns,
      getTotalSize,
      setColumnOrder,
    } = table;

    useEffect(() => {
      if (autoResetSelectedRows) {
        resetRowSelection(true);
      }
    }, [data, autoResetSelectedRows, resetRowSelection]);

    const selectedRows = filters
      ? getFilteredSelectedRowModel().flatRows
      : getSelectedRowModel().flatRows;

    useEffect(() => {
      if (onSelectedRowsChange) {
        onSelectedRowsChange({
          selectedRowsData: selectedRows.map((d) => d.original),
        });
      }
    }, [onSelectedRowsChange, selectedRows]);

    props.clearSelection?.(resetRowSelection);

    const selectedBarVisible =
      !!props.renderSelectedBar &&
      !!(selectedRows.length || props.displaySelectedBar);

    const totalSize =
      enableRowSelection
        ? CHECKBOX_COLUMN_SIZE + getTotalSize()
        : getTotalSize();

    const showColumnSelectorToolbar =
      showColumnSelector && columnSelectorOptions && onColumnVisibilityChange;

    const scrollContentMinWidth =
      totalSize +
      scrollBarSize +
      (responsive ? SCROLL_SPACER_WIDTH : 0) +
      (showColumnSelectorToolbar ? TOOLBAR_COLUMN_WIDTH : 0);

    const rowMinWidth = useUnifiedExpandableScroll
      ? scrollContentMinWidth
      : totalSize + scrollBarSize;

    const handleHeaderDragStart = useCallback(
      (e: React.DragEvent, columnId: string) => {
        if (
          !enableColumnReorder ||
          (stickyLeadingColumnId != null && columnId === stickyLeadingColumnId)
        ) {
          return;
        }
        e.dataTransfer.setData("text/plain", columnId);
        e.dataTransfer.effectAllowed = "move";
        setDraggedColumnId(columnId);
      },
      [enableColumnReorder, stickyLeadingColumnId]
    );

    const handleHeaderDragEnd = useCallback(() => {
      setDraggedColumnId(null);
      setDropTargetId(null);
    }, []);

    const handleHeaderDragOver = useCallback(
      (e: React.DragEvent, columnId: string) => {
        if (!enableColumnReorder || !draggedColumnId || draggedColumnId === columnId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropTargetId(columnId);
      },
      [enableColumnReorder, draggedColumnId]
    );

    const handleHeaderDragLeave = useCallback(() => {
      setDropTargetId(null);
    }, []);

    const handleHeaderDrop = useCallback(
      (e: React.DragEvent, targetColumnId: string) => {
        e.preventDefault();
        const sourceId = e.dataTransfer.getData("text/plain");
        if (
          !sourceId ||
          sourceId === targetColumnId ||
          (stickyLeadingColumnId != null &&
            (sourceId === stickyLeadingColumnId || targetColumnId === stickyLeadingColumnId))
        ) {
          setDraggedColumnId(null);
          setDropTargetId(null);
          return;
        }
        const currentOrder = columnOrder ?? getAllColumns().map((c) => c.id);
        const srcIdx = currentOrder.indexOf(sourceId);
        const tgtIdx = currentOrder.indexOf(targetColumnId);
        if (srcIdx === -1 || tgtIdx === -1) {
          setDraggedColumnId(null);
          setDropTargetId(null);
          return;
        }
        const next = [...currentOrder];
        next.splice(srcIdx, 1);
        next.splice(tgtIdx, 0, sourceId);
        const ordered =
          stickyLeadingColumnId != null
            ? [stickyLeadingColumnId, ...next.filter((id) => id !== stickyLeadingColumnId)]
            : next;
        if (onColumnOrderChange) {
          onColumnOrderChange(ordered);
        } else {
          setColumnOrder(ordered);
        }
        setDraggedColumnId(null);
        setDropTargetId(null);
      },
      [columnOrder, getAllColumns, onColumnOrderChange, setColumnOrder, stickyLeadingColumnId]
    );

    const onClickSort = useCallback(
      (column: Column<any>, e: React.MouseEvent) => {
        column.getToggleSortingHandler()?.(e);
      },
      []
    );

    const highlightRow = useCallback(
      (row: any) => {
        if (row.depth === 0)
          return props.highlightRow !== undefined && props.highlightRow === row.index;
        const parentIndex = row.id.split(".")[0];
        return (
          !!props.highlightSubRows?.length &&
          parentIndex === props.highlightRow?.toString() &&
          (props.highlightSubRows?.findIndex((i: number) => i === row.index) ?? -1) >= 0
        );
      },
      [props.highlightRow, props.highlightSubRows]
    );

    const isLeadingColumn = useCallback(
      (columnId: string) => !!stickyLeadingColumnId && columnId === stickyLeadingColumnId,
      [stickyLeadingColumnId]
    );

    const listHeight = useMemo(() => {
      if (shrinkToContent) {
        const rowCount = data?.length ?? 0;
        if (rowCount === 0) return minTableHeight ?? 80;
        return rowCount * rowHeight + 2;
      }
      if (listHeightOverride != null) return listHeightOverride;
      return getTableHeight(
        tableId,
        windowHeight,
        strictMinTableHeight,
        minTableHeight
      );
    }, [
      shrinkToContent,
      data?.length,
      listHeightOverride,
      tableId,
      windowHeight,
      strictMinTableHeight,
      minTableHeight,
      rowHeight,
    ]);

    const scrollListHeight = useMemo(() => {
      const footerHeight = renderTableFooter ? rowHeight : 0;
      return Math.max(0, listHeight - footerHeight);
    }, [listHeight, renderTableFooter, rowHeight]);

    const syncScrollToSelection = useCallback((scrollOffset: number) => {
      if (scrollSyncRef.current) return;
      scrollSyncRef.current = true;
      selectionListRef.current?.scrollTo(scrollOffset);
      requestAnimationFrame(() => {
        scrollSyncRef.current = false;
      });
    }, []);

    const syncScrollToData = useCallback((scrollOffset: number) => {
      if (scrollSyncRef.current) return;
      scrollSyncRef.current = true;
      dataListRef.current?.scrollTo(scrollOffset);
      requestAnimationFrame(() => {
        scrollSyncRef.current = false;
      });
    }, []);

    const rows = getRowModel().rows;
    const listItemKey = useCallback(
      (index: number, listRows: Row<any>[]) => listRows[index]?.id ?? String(index),
      []
    );

    const renderSelectionRow = useCallback(
      ({ index, style, data: listRows }: ListChildComponentProps<Row<any>[]>) => {
        const row = listRows[index];
        if (!row) return null;
        return (
          <div
            role="row"
            style={{
              ...style,
              display: "flex",
              alignItems: "stretch",
              minHeight: rowHeight,
            }}
            className={cn("xgs-data-table__row xgs-data-table__row--select-only")({
              "even-row": index % 2 !== 0,
            })}
          >
            <div
              role="cell"
              className="xgs-data-table__td xgs-data-table__td--select"
              style={{
                width: CHECKBOX_COLUMN_SIZE,
                minWidth: CHECKBOX_COLUMN_SIZE,
              }}
            >
              {row.getCanSelect() && (
                <input
                  type="checkbox"
                  checked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          </div>
        );
      },
      [rowHeight]
    );

    const renderRow = useCallback(
      ({ index, style, data: listRows }: ListChildComponentProps<Row<any>[]>) => {
        const row = listRows[index];
        if (!row) return null;
        return (
          <div
            role="row"
            style={{
              ...style,
              minWidth: totalSize + scrollBarSize,
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
            }}
            className={cn("xgs-data-table__row")({
              "cursor-pointer": !!cursorPointer,
              "even-row": index % 2 !== 0,
              "is-highlighted": highlightRow(row),
            })}
            onClick={() =>
              onRowClicked ? onRowClicked(row.original, index) : undefined
            }
          >
            {enableRowSelection && (
              <div
                role="cell"
                className="xgs-data-table__td xgs-data-table__td--select"
                style={{
                  width: CHECKBOX_COLUMN_SIZE,
                  minWidth: CHECKBOX_COLUMN_SIZE,
                }}
              >
                {row.getCanSelect() && (
                  <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            )}
            {row.getVisibleCells().map((cell) => (
              <div
                role="cell"
                key={cell.id}
                className={[
                  cn("xgs-data-table__td")({
                    "cursor-pointer":
                      !!cursorPointer &&
                      !!ignoreCellClick &&
                      cell.column.id !== ignoreCellClick,
                    "can-resize": !!(enableColumnResize && cell.column.getCanResize()),
                  }),
                  isLeadingColumn(cell.column.id) ? "xgs-data-table__td--leading" : "",
                ].filter(Boolean).join(" ")}
                style={{
                  width: cell.column.getSize(),
                  minWidth: cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                  maxWidth: cell.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                }}
                onClick={() =>
                  onRowClicked &&
                  ignoreCellClick &&
                  cell.column.id !== ignoreCellClick
                    ? onRowClicked(row.original)
                    : undefined
                }
              >
                <div className="xgs-data-table__cell-content">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
            ))}
            {responsive && (
              <div
                role="cell"
                className="xgs-data-table__scroll-spacer"
                aria-hidden
              />
            )}
          </div>
        );
      },
      [
        totalSize,
        scrollBarSize,
        cursorPointer,
        highlightRow,
        onRowClicked,
        enableRowSelection,
        ignoreCellClick,
        responsive,
        enableColumnResize,
        isLeadingColumn,
      ]
    );

    const dataColumnsSize = totalSize - (enableRowSelection ? CHECKBOX_COLUMN_SIZE : 0);

    const renderDataRow = useCallback(
      ({ index, style, data: listRows }: ListChildComponentProps<Row<any>[]>) => {
        const row = listRows[index];
        if (!row) return null;
        const handleRowClick = (e: React.MouseEvent) => {
          const target = e.target as HTMLElement;
          if (target.closest("a, button, [role='button'], input:not([type='checkbox'])")) {
            return;
          }
          if (rowClickTogglesSelection && enableRowSelection && row.getCanSelect()) {
            row.getToggleSelectedHandler()({ target: { checked: !row.getIsSelected() } } as React.ChangeEvent<HTMLInputElement>);
          }
          if (onRowClicked) onRowClicked(row.original, index);
        };
        return (
          <div
            role="row"
            style={{
              ...style,
              minWidth: dataColumnsSize + scrollBarSize,
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
            }}
            className={cn("xgs-data-table__row")({
              "cursor-pointer": !!cursorPointer || !!rowClickTogglesSelection,
              "even-row": index % 2 !== 0,
              "is-highlighted": highlightRow(row),
            })}
            onClick={handleRowClick}
          >
            {row.getVisibleCells().map((cell) => (
              <div
                role="cell"
                key={cell.id}
                className={cn("xgs-data-table__td")(
                  (cursorPointer || rowClickTogglesSelection) &&
                    ignoreCellClick &&
                    cell.column.id !== ignoreCellClick
                    ? "cursor-pointer"
                    : ""
                )}
                style={{
                  width: cell.column.getSize() || (cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH),
                  minWidth: cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                  maxWidth: cell.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                  flexShrink: 0,
                  flexGrow: 0,
                  boxSizing: "border-box",
                }}
              >
                <div className="xgs-data-table__cell-content">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
            ))}
            {responsive && (
              <div
                role="cell"
                className="xgs-data-table__scroll-spacer"
                aria-hidden
                style={{ width: 16, minWidth: 16, flexShrink: 0 }}
              />
            )}
          </div>
        );
      },
      [
        dataColumnsSize,
        scrollBarSize,
        cursorPointer,
        highlightRow,
        onRowClicked,
        ignoreCellClick,
        responsive,
        rowClickTogglesSelection,
        enableRowSelection,
      ]
    );

    const virtualRowCount =
      infiniteScroll && infiniteScrollHasNext ? rows.length + 1 : rows.length;

    const loadMoreItems = useCallback(() => {
      if (!infiniteScrollLoading && onInfiniteScroll) {
        onInfiniteScroll();
      }
    }, [infiniteScrollLoading, onInfiniteScroll]);

    const isItemLoaded = useCallback(
      (index: number) => !infiniteScrollHasNext || index < rows.length,
      [infiniteScrollHasNext, rows.length]
    );

    const renderInfiniteLoaderRow = useCallback(
      (listProps: ListChildComponentProps<Row<any>[]>) => {
        const { index, style, data: listRows } = listProps;
        if (infiniteScroll && infiniteScrollHasNext && index >= listRows.length) {
          return (
            <div
              role="row"
              style={{
                ...style,
                minWidth: totalSize + scrollBarSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="xgs-data-table__row xgs-data-table__row--infinite-loading"
            >
              <div role="cell" className="xgs-data-table__td xgs-data-table__infinite-loading-cell">
                {infiniteScrollLoading ? "Loading..." : ""}
              </div>
            </div>
          );
        }
        return renderRow(listProps);
      },
      [
        infiniteScroll,
        infiniteScrollHasNext,
        infiniteScrollLoading,
        renderRow,
        scrollBarSize,
        totalSize,
      ]
    );

    const renderInfiniteFixedList = (
      height: number,
      width: number,
      listHeight?: number
    ) => {
      if (!infiniteScroll) {
        return (
          <FixedSizeList
            className="xgs-data-table__fixed-size-list"
            height={listHeight ?? height}
            width={width}
            itemCount={rows.length}
            itemData={rows}
            itemKey={listItemKey}
            itemSize={rowHeight}
          >
            {renderRow}
          </FixedSizeList>
        );
      }

      return (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={50000}
          loadMoreItems={loadMoreItems}
          minimumBatchSize={25}
        >
          {({ onItemsRendered, ref }) => (
            <FixedSizeList
              ref={ref}
              className="xgs-data-table__fixed-size-list"
              height={listHeight ?? height}
              width={width}
              itemCount={virtualRowCount}
              itemData={rows}
              itemKey={listItemKey}
              itemSize={rowHeight}
              onItemsRendered={onItemsRendered}
            >
              {renderInfiniteLoaderRow}
            </FixedSizeList>
          )}
        </InfiniteLoader>
      );
    };

    const resolvedTotals = useMemo(() => {
      if (!totals?.length) return [];
      const visibleRows = rows.map((row) => row.original);
      return totals.map((total) => {
        if (total.count) {
          return { ...total, value: visibleRows.length };
        }
        if (total.sumField) {
          return {
            ...total,
            value: visibleRows.reduce(
              (sum, row) =>
                sum + (Number((row as Record<string, unknown>)[total.sumField!]) || 0),
              0
            ),
          };
        }
        return total;
      });
    }, [totals, rows]);

    const renderTableFooterContent = () => {
      if (!renderTableFooter || !data?.length) return null;
      return (
        <div className="xgs-data-table__footer">
          {renderTableFooter({ rows: rows.map((row) => row.original) })}
        </div>
      );
    };

    const renderShrinkRows = () => (
      <div className="xgs-data-table__body-scroll xgs-data-table__body-scroll--shrink">
        {rows.map((row, index) => {
          const handleRowClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest("a, button, [role='button'], input:not([type='checkbox'])")) {
              return;
            }
            if (onRowClicked) onRowClicked(row.original, index);
          };
          return (
            <div
              key={row.id}
              role="row"
              style={{
                minWidth: totalSize + scrollBarSize,
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                minHeight: rowHeight,
              }}
              className={cn("xgs-data-table__row")({
                "cursor-pointer": !!cursorPointer,
                "even-row": index % 2 !== 0,
                "is-highlighted": highlightRow(row),
              })}
              onClick={handleRowClick}
            >
              {row.getVisibleCells().map((cell) => (
                <div
                  role="cell"
                  key={cell.id}
                  className={[
                    cn("xgs-data-table__td")({
                      "cursor-pointer":
                        !!cursorPointer &&
                        !!ignoreCellClick &&
                        cell.column.id !== ignoreCellClick,
                    }),
                    isLeadingColumn(cell.column.id) ? "xgs-data-table__td--leading" : "",
                  ].filter(Boolean).join(" ")}
                  style={{
                    width: cell.column.getSize() || (cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH),
                    minWidth: cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                    maxWidth: cell.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                    flexShrink: 0,
                    flexGrow: 0,
                    boxSizing: "border-box",
                  }}
                >
                  <div className="xgs-data-table__cell-content">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </div>
              ))}
              {responsive && (
                <div role="cell" className="xgs-data-table__scroll-spacer" aria-hidden />
              )}
              {showColumnSelectorToolbar && (
                <div
                  role="cell"
                  className="xgs-data-table__td xgs-data-table__td--toolbar"
                  aria-hidden
                  style={{
                    width: TOOLBAR_COLUMN_WIDTH,
                    minWidth: TOOLBAR_COLUMN_WIDTH,
                    flexShrink: 0,
                    flexGrow: 0,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    );

    const renderExpandableBodyRows = () =>
      rows.map((row, index) => {
        const handleRowClick = (e: React.MouseEvent) => {
          const target = e.target as HTMLElement;
          if (target.closest("a, button, [role='button'], input:not([type='checkbox'])")) {
            return;
          }
          if (rowClickTogglesSelection && enableRowSelection && row.getCanSelect()) {
            row.getToggleSelectedHandler()({
              target: { checked: !row.getIsSelected() },
            } as React.ChangeEvent<HTMLInputElement>);
          }
          if (onRowClicked) onRowClicked(row.original, index);
        };
        return (
          <React.Fragment key={row.id}>
            <div
              role="row"
              style={{
                minWidth: rowMinWidth,
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                minHeight: rowHeight,
              }}
              className={cn("xgs-data-table__row")({
                "cursor-pointer": !!cursorPointer || !!rowClickTogglesSelection,
                "even-row": index % 2 !== 0,
                "is-highlighted": highlightRow(row),
              })}
              onClick={handleRowClick}
            >
              {enableRowSelection && (
                <div
                  role="cell"
                  className="xgs-data-table__td xgs-data-table__td--select"
                  style={{
                    width: CHECKBOX_COLUMN_SIZE,
                    minWidth: CHECKBOX_COLUMN_SIZE,
                  }}
                >
                  {row.getCanSelect() && (
                    <input
                      type="checkbox"
                      checked={row.getIsSelected()}
                      onChange={row.getToggleSelectedHandler()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              )}
              {row.getVisibleCells().map((cell) => (
                <div
                  role="cell"
                  key={cell.id}
                  className={[
                    cn("xgs-data-table__td")({
                      "cursor-pointer":
                        !!(cursorPointer || rowClickTogglesSelection) &&
                        !!ignoreCellClick &&
                        cell.column.id !== ignoreCellClick,
                    }),
                    isLeadingColumn(cell.column.id) ? "xgs-data-table__td--leading" : "",
                  ].filter(Boolean).join(" ")}
                  style={{
                    width: cell.column.getSize() || (cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH),
                    minWidth: cell.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                    maxWidth: cell.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                    flexShrink: 0,
                    flexGrow: 0,
                    boxSizing: "border-box",
                  }}
                >
                  <div className="xgs-data-table__cell-content">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </div>
              ))}
              {responsive && (
                <div role="cell" className="xgs-data-table__scroll-spacer" aria-hidden />
              )}
              {showColumnSelectorToolbar && (
                <div
                  role="cell"
                  className="xgs-data-table__td xgs-data-table__td--toolbar"
                  aria-hidden
                  style={{
                    width: TOOLBAR_COLUMN_WIDTH,
                    minWidth: TOOLBAR_COLUMN_WIDTH,
                    flexShrink: 0,
                    flexGrow: 0,
                  }}
                />
              )}
            </div>
            {row.getIsExpanded() && renderSubComponent && (
              <div role="row" className="xgs-data-table__sub-component-row">
                <div role="cell" style={{ width: "100%", padding: 0 }}>
                  {renderSubComponent(row)}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      });

    const renderScrollableHeaderRow = () => {
      const headerGroup = getHeaderGroups()[0];
      if (!headerGroup) return null;

      return (
        <div
          role="row"
          className={[
            "xgs-data-table__header-row",
            "xgs-data-table__header-row--scroll",
            props.customHeaderRowClass,
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            minWidth: rowMinWidth,
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
          }}
        >
          {headerGroup.headers
            .filter((header) => header.column.id !== "selection")
            .map((header) => {
              const canResize =
                enableColumnResize &&
                header.column.getCanResize() &&
                !isLeadingColumn(header.column.id);
              const canReorder =
                enableColumnReorder &&
                header.column.id !== "selection" &&
                !isLeadingColumn(header.column.id);
              const isDragging = draggedColumnId === header.column.id;
              const isDropTarget = dropTargetId === header.column.id;

              return (
                <div
                  role="columnheader"
                  key={header.id}
                  className={[
                    cn("xgs-data-table__th")({
                      "is-dragging": isDragging,
                      "is-drop-target": isDropTarget,
                      "can-resize": canResize,
                    }),
                    isLeadingColumn(header.column.id) ? "xgs-data-table__th--leading" : "",
                  ].filter(Boolean).join(" ")}
                  style={{
                    width: header.getSize() || (header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH),
                    minWidth: header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                    maxWidth: header.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                    flexShrink: 0,
                    flexGrow: 0,
                    boxSizing: "border-box",
                  }}
                  onDragOver={canReorder ? (e) => handleHeaderDragOver(e, header.column.id) : undefined}
                  onDragLeave={canReorder ? handleHeaderDragLeave : undefined}
                  onDrop={canReorder ? (e) => handleHeaderDrop(e, header.column.id) : undefined}
                >
                  <div className="xgs-data-table__th-inner">
                    {canReorder && (
                      <span
                        className="xgs-data-table__drag-handle"
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => handleHeaderDragStart(e, header.column.id)}
                        onDragEnd={handleHeaderDragEnd}
                        aria-label="Drag to reorder column"
                        title="Drag to reorder column"
                      />
                    )}
                    <div
                      className={
                        sorting && header.column.getCanSort()
                          ? "xgs-data-table__th-sortable"
                          : ""
                      }
                      onClick={
                        interactiveHeader && sorting && header.column.getCanSort()
                          ? (e) => onClickSort(header.column, e)
                          : undefined
                      }
                    >
                      <span className="xgs-data-table__th-label">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {sorting && header.column.getCanSort() && (
                        <span className="xgs-data-table__sort-icons">
                          {(header.column.getIsSorted() === "asc" || !header.column.getIsSorted()) && (
                            <span className="xgs-data-table__sort-icon">
                              <ArrowUpIcon />
                            </span>
                          )}
                          {(header.column.getIsSorted() === "desc" || !header.column.getIsSorted()) && (
                            <span className="xgs-data-table__sort-icon">
                              <ArrowDownIcon />
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    {canResize && (
                      <div
                        className="xgs-data-table__resize-handle"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          header.getResizeHandler()(e);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          header.getResizeHandler()(e);
                        }}
                        title="Resize column"
                      />
                    )}
                  </div>
                  {filters && header.column.getCanFilter() ? (
                    <div className="xgs-data-table__th-filter">
                      <Filter column={header.column} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          {responsive && (
            <div
              role="columnheader"
              className="xgs-data-table__scroll-spacer"
              aria-hidden
              style={{
                width: showColumnSelector && columnSelectorOptions && onColumnVisibilityChange ? 4 : 16,
                flexShrink: 0,
                flexGrow: 0,
              }}
            />
          )}
          {showColumnSelectorToolbar && (
            <div
              role="columnheader"
              className="xgs-data-table__th xgs-data-table__th--toolbar"
              aria-hidden
            >
              <DataTableToolbar
                columnOptions={columnSelectorOptions}
                columnVisibility={columnVisibility ?? {}}
                onColumnVisibilityChange={onColumnVisibilityChange}
              />
            </div>
          )}
        </div>
      );
    };

    const renderExpandableRows = () => {
      if (useUnifiedExpandableScroll) {
        return (
          <div className="xgs-data-table__unified-scroll-outer">
            <div
              className="xgs-data-table__unified-scroll"
              style={{
                overflow: "auto",
                height: autoHeight ? "100%" : scrollListHeight,
                maxHeight: autoHeight ? undefined : scrollListHeight,
              }}
            >
              <div
                className="xgs-data-table__unified-scroll-inner"
                style={{ minWidth: rowMinWidth, width: "max-content" }}
              >
                {renderScrollableHeaderRow()}
                {renderExpandableBodyRows()}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          className="xgs-data-table__body-scroll xgs-data-table__body-scroll--expandable"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            height: autoHeight ? "100%" : scrollListHeight,
            maxHeight: autoHeight ? undefined : scrollListHeight,
          }}
        >
          {renderExpandableBodyRows()}
        </div>
      );
    };

    return (
      <div
        className={cn("xgs-data-table__wrapper")({
          loading: isLoading,
          "sticky-headers": !!stickyHeaders && !useUnifiedExpandableScroll,
          "sticky-select-column": !!enableRowSelection,
          "sticky-leading-column": !!stickyLeadingColumnId && !useUnifiedExpandableScroll,
          "unified-expandable-scroll": useUnifiedExpandableScroll,
        })}
        style={{
          maxWidth: props.maxTableWidth,
          ...(autoHeight && data?.length
            ? { height: listHeight + 40, minHeight: listHeight + 40 }
            : autoHeight
            ? { minHeight: listHeight + 40 }
            : {}),
        }}
      >
        {isLoading && !keepTableOnLoading && (
          <div className="xgs-data-table__loading">
            <Loading isLoading />
          </div>
        )}

        {(!isLoading || isBlocked || keepTableOnLoading) && (
          <>
            {((isLoading && keepTableOnLoading) || isBlocked) && (
              <div className="xgs-data-table__loading-background">
                <div className="xgs-data-table__loading xgs-data-table__loading--over">
                  <Loading isLoading={!!isLoading} />
                </div>
              </div>
            )}
          <div
            ref={ref}
            role="table"
            aria-rowcount={data?.length ?? 0}
            aria-colcount={
              (enableRowSelection ? 1 : 0) +
              (getHeaderGroups()[0]?.headers?.length ?? 0) +
              (responsive ? 1 : 0)
            }
            className={cn("xgs-data-table")(
              {
                responsive: !!responsive,
                "sticky-headers": !!stickyHeaders,
              },
              tableId
            )}
            style={{
              width:
                useUnifiedExpandableScroll || (enableRowSelection && responsive) || responsive
                  ? "100%"
                  : totalSize + scrollBarSize,
              minWidth: useUnifiedExpandableScroll
                ? 0
                : enableRowSelection
                  ? "0"
                  : totalSize + scrollBarSize,
            }}
          >
            {!(enableRowSelection && data?.length) && !useUnifiedExpandableScroll && (
            <div className="xgs-data-table__thead" role="rowgroup">
            {getHeaderGroups().slice(0, 1).map((headerGroup) => (
              <div
                key={headerGroup.id}
                role="row"
                className={[
                  "xgs-data-table__header-row",
                  props.customHeaderRowClass,
                ].filter(Boolean).join(" ")}
                style={{
                  minWidth: totalSize + scrollBarSize,
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                }}
              >
                {enableRowSelection && (
                  <div
                    role="columnheader"
                    className="xgs-data-table__th xgs-data-table__th--select"
                    style={{
                      width: CHECKBOX_COLUMN_SIZE,
                      minWidth: CHECKBOX_COLUMN_SIZE,
                      flexShrink: 0,
                      flexGrow: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={getIsAllRowsSelected()}
                      onChange={getToggleAllRowsSelectedHandler()}
                      aria-label="Select all"
                    />
                  </div>
                )}

                {headerGroup.headers
                  .filter((header) => header.column.id !== "selection")
                  .map((header) => {
                  const canResize =
                    enableColumnResize &&
                    header.column.getCanResize() &&
                    !isLeadingColumn(header.column.id);
                  const canReorder =
                    enableColumnReorder &&
                    header.column.id !== "selection" &&
                    !isLeadingColumn(header.column.id);
                  const isDragging = draggedColumnId === header.column.id;
                  const isDropTarget = dropTargetId === header.column.id;

                  return (
                    <div
                      role="columnheader"
                      key={header.id}
                      className={[
                        cn("xgs-data-table__th")({
                          "is-dragging": isDragging,
                          "is-drop-target": isDropTarget,
                          "can-resize": canResize,
                        }),
                        isLeadingColumn(header.column.id) ? "xgs-data-table__th--leading" : "",
                      ].filter(Boolean).join(" ")}
                      style={{
                        width: header.getSize() || (header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH),
                        minWidth: header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                        maxWidth: header.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                        flexShrink: 0,
                        flexGrow: 0,
                        boxSizing: "border-box",
                      }}
                      onDragOver={canReorder ? (e) => handleHeaderDragOver(e, header.column.id) : undefined}
                      onDragLeave={canReorder ? handleHeaderDragLeave : undefined}
                      onDrop={canReorder ? (e) => handleHeaderDrop(e, header.column.id) : undefined}
                    >
                      <div className="xgs-data-table__th-inner">
                        {canReorder && (
                          <span
                            className="xgs-data-table__drag-handle"
                            role="button"
                            tabIndex={0}
                            draggable
                            onDragStart={(e) => handleHeaderDragStart(e, header.column.id)}
                            onDragEnd={handleHeaderDragEnd}
                            aria-label="Drag to reorder column"
                            title="Drag to reorder column"
                            onKeyDown={(e) => {
                              if (e.key === " " || e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.focus();
                              }
                            }}
                          />
                        )}
                        <div
                          className={
                            sorting && header.column.getCanSort()
                              ? "xgs-data-table__th-sortable"
                              : ""
                          }
                          onClick={
                            interactiveHeader && sorting && header.column.getCanSort()
                              ? (e) => onClickSort(header.column, e)
                              : undefined
                          }
                        >
                          <span className="xgs-data-table__th-label">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {sorting && header.column.getCanSort() && (
                            <span className="xgs-data-table__sort-icons">
                              {(header.column.getIsSorted() === "asc" || !header.column.getIsSorted()) && (
                                <span className="xgs-data-table__sort-icon">
                                  <ArrowUpIcon />
                                </span>
                              )}
                              {(header.column.getIsSorted() === "desc" || !header.column.getIsSorted()) && (
                                <span className="xgs-data-table__sort-icon">
                                  <ArrowDownIcon />
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        {canResize && (
                          <div
                            className="xgs-data-table__resize-handle"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              header.getResizeHandler()(e);
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              header.getResizeHandler()(e);
                            }}
                            title="Resize column"
                          />
                        )}
                      </div>
                        {filters && header.column.getCanFilter() ? (
                          <div className="xgs-data-table__th-filter">
                            <Filter column={header.column} />
                          </div>
                        ) : null}
                    </div>
                  );
                })}
                {responsive && (
                  <div
                    role="columnheader"
                    className="xgs-data-table__scroll-spacer"
                    aria-hidden
                    style={{
                      width: showColumnSelector && columnSelectorOptions && onColumnVisibilityChange ? 4 : 16,
                      flexShrink: 0,
                      flexGrow: 0,
                    }}
                  />
                )}
                {showColumnSelector && columnSelectorOptions && onColumnVisibilityChange && (
                  <div
                    role="columnheader"
                    className="xgs-data-table__th xgs-data-table__th--toolbar"
                    aria-hidden
                  >
                    <DataTableToolbar
                      columnOptions={columnSelectorOptions}
                      columnVisibility={columnVisibility ?? {}}
                      onColumnVisibilityChange={onColumnVisibilityChange}
                    />
                  </div>
                )}
              </div>
            ))}
            </div>
            )}

            <div
              role="rowgroup"
              className={cn("xgs-data-table__content")({ empty: !data?.length })}
              style={
                shrinkToContent
                  ? { height: "auto", flex: "none" }
                  : !autoHeight && data?.length
                  ? { height: scrollListHeight, flex: "none" }
                  : undefined
              }
            >
              {data?.length > 0 ? (
                <div
                  style={{
                    height: shrinkToContent ? "auto" : autoHeight ? "100%" : scrollListHeight,
                    minHeight: 0,
                  }}
                  className="xgs-data-table__content-inner"
                >
                  {shrinkToContent ? (
                    renderShrinkRows()
                  ) : renderSubComponent ? (
                    renderExpandableRows()
                  ) : enableRowSelection ? (
                    <div className="xgs-data-table__body-scroll">
                      <div
                        className="xgs-data-table__body-scroll-inner"
                        style={{
                          width: totalSize + scrollBarSize,
                          minHeight: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          role="row"
                          className={[
                            "xgs-data-table__header-row",
                            props.customHeaderRowClass,
                          ].filter(Boolean).join(" ")}
                          style={{
                            width: totalSize + scrollBarSize,
                            minWidth: totalSize + scrollBarSize,
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {enableRowSelection && (
                            <div
                              role="columnheader"
                              className="xgs-data-table__th xgs-data-table__th--select"
                              style={{
                                width: CHECKBOX_COLUMN_SIZE,
                                minWidth: CHECKBOX_COLUMN_SIZE,
                                flexShrink: 0,
                                flexGrow: 0,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={getIsAllRowsSelected()}
                                onChange={getToggleAllRowsSelectedHandler()}
                                aria-label="Select all"
                              />
                            </div>
                          )}
                          {getHeaderGroups()[0]?.headers
                            .filter((h) => h.column.id !== "selection")
                            .map((header) => {
                              const canResize =
                                enableColumnResize &&
                                header.column.getCanResize() &&
                                !isLeadingColumn(header.column.id);
                              const canReorder =
                                enableColumnReorder &&
                                header.column.id !== "selection" &&
                                !isLeadingColumn(header.column.id);
                              const isDragging = draggedColumnId === header.column.id;
                              const isDropTarget = dropTargetId === header.column.id;
                              return (
                                <div
                                  role="columnheader"
                                  key={header.id}
                                  className={[
                                    cn("xgs-data-table__th")({
                                      "is-dragging": isDragging,
                                      "is-drop-target": isDropTarget,
                                      "can-resize": canResize,
                                    }),
                                    isLeadingColumn(header.column.id) ? "xgs-data-table__th--leading" : "",
                                  ].filter(Boolean).join(" ")}
                                  style={{
                                    width: header.getSize() || (header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH),
                                    minWidth: header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_WIDTH,
                                    maxWidth: header.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_WIDTH,
                                    flexShrink: 0,
                                    flexGrow: 0,
                                    boxSizing: "border-box",
                                  }}
                                  onDragOver={canReorder ? (e) => handleHeaderDragOver(e, header.column.id) : undefined}
                                  onDragLeave={canReorder ? handleHeaderDragLeave : undefined}
                                  onDrop={canReorder ? (e) => handleHeaderDrop(e, header.column.id) : undefined}
                                >
                                  <div className="xgs-data-table__th-inner">
                                    {canReorder && (
                                      <span
                                        className="xgs-data-table__drag-handle"
                                        role="button"
                                        tabIndex={0}
                                        draggable
                                        onDragStart={(e) => handleHeaderDragStart(e, header.column.id)}
                                        onDragEnd={handleHeaderDragEnd}
                                        aria-label="Drag to reorder column"
                                        title="Drag to reorder column"
                                        onKeyDown={(e) => {
                                          if (e.key === " " || e.key === "Enter") {
                                            e.preventDefault();
                                            e.currentTarget.focus();
                                          }
                                        }}
                                      />
                                    )}
                                    <div
                                      className={
                                        sorting && header.column.getCanSort()
                                          ? "xgs-data-table__th-sortable"
                                          : ""
                                      }
                                      onClick={
                                        interactiveHeader && sorting && header.column.getCanSort()
                                          ? (e) => onClickSort(header.column, e)
                                          : undefined
                                      }
                                    >
                                      <span className="xgs-data-table__th-label">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                      </span>
                                      {sorting && header.column.getCanSort() && (
                                        <span className="xgs-data-table__sort-icons">
                                          {(header.column.getIsSorted() === "asc" || !header.column.getIsSorted()) && (
                                            <span className="xgs-data-table__sort-icon">
                                              <ArrowUpIcon />
                                            </span>
                                          )}
                                          {(header.column.getIsSorted() === "desc" || !header.column.getIsSorted()) && (
                                            <span className="xgs-data-table__sort-icon">
                                              <ArrowDownIcon />
                                            </span>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    {canResize && (
                                      <div
                                        className="xgs-data-table__resize-handle"
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                          header.getResizeHandler()(e);
                                        }}
                                        onTouchStart={(e) => {
                                          e.stopPropagation();
                                          header.getResizeHandler()(e);
                                        }}
                                        title="Resize column"
                                      />
                                    )}
                                  </div>
                                  {filters && header.column.getCanFilter() ? (
                                    <div className="xgs-data-table__th-filter">
                                      <Filter column={header.column} />
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          {responsive && (
                            <div
                              role="columnheader"
                              className="xgs-data-table__scroll-spacer"
                              aria-hidden
                              style={{
                                width: showColumnSelector && columnSelectorOptions && onColumnVisibilityChange ? 4 : 16,
                                flexShrink: 0,
                                flexGrow: 0,
                              }}
                            />
                          )}
                          {showColumnSelector && columnSelectorOptions && onColumnVisibilityChange && (
                            <div
                              role="columnheader"
                              className="xgs-data-table__th xgs-data-table__th--toolbar"
                              aria-hidden
                            >
                              <DataTableToolbar
                                columnOptions={columnSelectorOptions}
                                columnVisibility={columnVisibility ?? {}}
                                onColumnVisibilityChange={onColumnVisibilityChange}
                              />
                            </div>
                          )}
                        </div>
                        <div
                          className="xgs-data-table__body-flex"
                          style={{
                            width: totalSize + scrollBarSize,
                            flex: "1 1 0",
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                        <div className="xgs-data-table__selection-column">
                          {autoHeight ? (
                            <AutoSizer>
                              {({ height, width }) => (
                                <FixedSizeList
                                  ref={selectionListRef}
                                  className="xgs-data-table__fixed-size-list xgs-data-table__fixed-size-list--select"
                                  height={height}
                                  width={CHECKBOX_COLUMN_SIZE}
                                  itemCount={rows.length}
                                  itemData={rows}
                                  itemKey={listItemKey}
                                  itemSize={rowHeight}
                                  onScroll={({ scrollOffset }) =>
                                    syncScrollToData(scrollOffset)
                                  }
                                >
                                  {renderSelectionRow}
                                </FixedSizeList>
                              )}
                            </AutoSizer>
                          ) : (
                            <FixedSizeList
                              ref={selectionListRef}
                              className="xgs-data-table__fixed-size-list xgs-data-table__fixed-size-list--select"
                              height={scrollListHeight}
                              width={CHECKBOX_COLUMN_SIZE}
                              itemCount={rows.length}
                              itemData={rows}
                              itemKey={listItemKey}
                              itemSize={rowHeight}
                              onScroll={({ scrollOffset }) =>
                                syncScrollToData(scrollOffset)
                              }
                            >
                              {renderSelectionRow}
                            </FixedSizeList>
                          )}
                        </div>
                        <div className="xgs-data-table__data-list-wrapper">
                          {autoHeight ? (
                            <AutoSizer>
                              {({ height, width }) => (
                                <FixedSizeList
                                  ref={dataListRef}
                                  className="xgs-data-table__fixed-size-list"
                                  height={height}
                                  width={Math.max(
                                    width,
                                    dataColumnsSize + scrollBarSize
                                  )}
                                  itemCount={rows.length}
                                  itemData={rows}
                                  itemKey={listItemKey}
                                  itemSize={rowHeight}
                                  onScroll={({ scrollOffset }) =>
                                    syncScrollToSelection(scrollOffset)
                                  }
                                >
                                  {renderDataRow}
                                </FixedSizeList>
                              )}
                            </AutoSizer>
                          ) : (
                            <FixedSizeList
                              ref={dataListRef}
                              className="xgs-data-table__fixed-size-list"
                              height={scrollListHeight}
                              width={dataColumnsSize + scrollBarSize}
                              itemCount={rows.length}
                              itemData={rows}
                              itemKey={listItemKey}
                              itemSize={rowHeight}
                              onScroll={({ scrollOffset }) =>
                                syncScrollToSelection(scrollOffset)
                              }
                            >
                              {renderDataRow}
                            </FixedSizeList>
                          )}
                        </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {autoHeight ? (
                        <AutoSizer>
                          {({ height, width }) =>
                            renderInfiniteFixedList(
                              height,
                              responsive
                                ? Math.max(width, totalSize + scrollBarSize)
                                : totalSize + scrollBarSize
                            )
                          }
                        </AutoSizer>
                      ) : (
                        renderInfiniteFixedList(
                          scrollListHeight,
                          totalSize + scrollBarSize,
                          scrollListHeight
                        )
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div
                  role="row"
                  className="xgs-data-table__row xgs-data-table__row--empty"
                >
                  <div
                    role="cell"
                    className="xgs-data-table__empty"
                    style={{ width: "100%" }}
                  >
                    {!isLoading &&
                      (noResultsComponent ?? (
                        <div>{noResultsText ?? "No records to display"}</div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

            {renderTableFooterContent()}

            {resolvedTotals.length > 0 && !renderTableFooter && (
              <div className="xgs-data-table__totals" style={{ width: totalSize + scrollBarSize }}>
                <TableTotal totals={resolvedTotals} data={rows.map((row) => row.original)} />
              </div>
            )}
          </>
        )}

        {selectedBarVisible && (
          <SelectedBar ref={selectedBarRef}>
            {props.renderSelectedBar?.(selectedRows.map((r) => r.original), toggleAllRowsSelected)}
          </SelectedBar>
        )}
      </div>
    );
  })
);

export default DataTable;
